// Security Manager - Full Production Version
// Enhanced security features for the website

export class SecurityManager {
    constructor() {
        this.initialized = false;
        this.violations = [];
        this.suspiciousActivity = 0;
        this.csrfToken = null;
        this.rateLimits = new Map();
        this.blockedIPs = new Set();
        this.securityHeaders = {};
    }

    init() {
        if (this.initialized) return;
        
        try {
            // Initialize CSRF protection
            this.initCSRFProtection();
            
            // Set up Content Security Policy
            this.setupCSP();
            
            // Monitor for security violations
            this.setupSecurityMonitoring();
            
            // Set up XSS protection
            this.setupXSSProtection();
            
            // Initialize rate limiting
            this.setupRateLimiting();
            
            // Set up clickjacking protection
            this.setupClickjackingProtection();
            
            // Monitor for suspicious patterns
            this.setupSuspiciousActivityDetection();
            
            // Set up secure headers
            this.setupSecurityHeaders();
            
            this.initialized = true;
            console.log('Security Manager initialized successfully');
            
        } catch (error) {
            console.error('Failed to initialize Security Manager:', error);
            this.handleSecurityError(error);
        }
    }

    initCSRFProtection() {
        // Generate CSRF token
        this.csrfToken = this.generateSecureToken();
        
        // Add to all forms
        document.addEventListener('DOMContentLoaded', () => {
            this.addCSRFToForms();
        });
        
        // Add to AJAX requests
        this.interceptXHR();
        this.interceptFetch();
    }

    generateSecureToken() {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    addCSRFToForms() {
        document.querySelectorAll('form').forEach(form => {
            if (!form.querySelector('input[name="csrf_token"]')) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'csrf_token';
                input.value = this.csrfToken;
                form.appendChild(input);
            }
        });
    }

    interceptXHR() {
        const originalOpen = XMLHttpRequest.prototype.open;
        const self = this;
        
        XMLHttpRequest.prototype.open = function(method, url, ...args) {
            this.addEventListener('loadstart', function() {
                // Skip CSRF token for Jitsu analytics calls
                const isJitsuCall = url && (
                    url.includes('jitsu.com') || 
                    url.includes('cmd3cb4mh00003b7spcxcqb1u.d.jitsu.com')
                );
                
                if (method.toUpperCase() !== 'GET' && !isJitsuCall) {
                    this.setRequestHeader('X-CSRF-Token', self.csrfToken);
                }
            });
            return originalOpen.apply(this, [method, url, ...args]);
        };
    }

    interceptFetch() {
        const originalFetch = window.fetch;
        const self = this;
        
        window.fetch = function(url, options = {}) {
            // Skip CSRF token for Jitsu analytics calls
            const isJitsuCall = url && (
                url.includes('jitsu.com') || 
                url.includes('cmd3cb4mh00003b7spcxcqb1u.d.jitsu.com')
            );
            
            if (options.method && options.method.toUpperCase() !== 'GET' && !isJitsuCall) {
                options.headers = {
                    ...options.headers,
                    'X-CSRF-Token': self.csrfToken
                };
            }
            return originalFetch.apply(this, [url, options]);
        };
    }

    setupCSP() {
        // Monitor CSP violations
        document.addEventListener('securitypolicyviolation', (e) => {
            this.handleCSPViolation(e);
        });
        
        // Set CSP meta tag if not present
        if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
            const meta = document.createElement('meta');
            meta.setAttribute('http-equiv', 'Content-Security-Policy');
            meta.setAttribute('content', this.getCSPPolicy());
            document.head.appendChild(meta);
        }
    }

    getCSPPolicy() {
        const jitsuDomain = import.meta.env.VITE_JITSU_HOST || import.meta.env.VITE_JITSU_DOMAIN || 'https://cmd3cb4mh00003b7spcxcqb1u.d.jitsu.com';
        
        return [
            "default-src 'self'",
            "script-src 'self' 'unsafe-inline' https://cdn.jsdelivr.net https://unpkg.com https://www.googletagmanager.com",
            "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
            "font-src 'self' https://fonts.gstatic.com",
            "img-src 'self' data: https:",
            `connect-src 'self' https://*.amazonaws.com ${jitsuDomain}`,
            "frame-ancestors 'none'",
            "base-uri 'self'",
            "form-action 'self'",
            "upgrade-insecure-requests"
        ].join('; ');
    }

    handleCSPViolation(e) {
        const violation = {
            type: 'CSP',
            directive: e.violatedDirective,
            blockedURI: e.blockedURI,
            lineNumber: e.lineNumber,
            sourceFile: e.sourceFile,
            timestamp: new Date().toISOString()
        };
        
        this.violations.push(violation);
        console.warn('CSP Violation:', violation);
        
        // Send to analytics if available
        if (window.analytics) {
            window.analytics.trackEvent('Security', 'CSPViolation', e.violatedDirective);
        }
    }

    setupSecurityMonitoring() {
        // Monitor for potential XSS attempts
        this.monitorDOMChanges();
        
        // Monitor for suspicious network requests
        this.monitorNetworkRequests();
        
        // Monitor for console manipulation
        this.protectConsole();
        
        // Monitor for debugging attempts
        this.detectDebugger();
    }

    monitorDOMChanges() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) { // Element node
                            this.checkElementSecurity(node);
                        }
                    });
                }
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['src', 'href', 'action', 'formaction', 'onclick', 'onload', 'onerror']
        });
    }

    checkElementSecurity(element) {
        // Check for suspicious attributes
        const suspiciousAttrs = ['onclick', 'onmouseover', 'onerror', 'onload'];
        suspiciousAttrs.forEach(attr => {
            if (element.hasAttribute(attr)) {
                const value = element.getAttribute(attr);
                if (this.isSuspiciousCode(value)) {
                    console.warn('Suspicious attribute detected:', attr, value);
                    element.removeAttribute(attr);
                    this.suspiciousActivity++;
                }
            }
        });
        
        // Check for javascript: URLs
        ['href', 'src', 'action'].forEach(attr => {
            const value = element.getAttribute(attr);
            if (value && value.toLowerCase().includes('javascript:')) {
                console.warn('JavaScript URL detected:', attr, value);
                element.removeAttribute(attr);
                this.suspiciousActivity++;
            }
        });
    }

    isSuspiciousCode(code) {
        const suspiciousPatterns = [
            /eval\s*\(/,
            /document\.write/,
            /innerHTML\s*=/,
            /outerHTML\s*=/,
            /document\.cookie/,
            /localStorage/,
            /sessionStorage/,
            /<script/i,
            /on\w+\s*=/i
        ];
        
        return suspiciousPatterns.some(pattern => pattern.test(code));
    }

    monitorNetworkRequests() {
        // Monitor fetch requests
        const originalFetch = window.fetch;
        window.fetch = (...args) => {
            const [url, options] = args;
            
            if (this.isBlockedURL(url)) {
                console.error('Blocked request to:', url);
                return Promise.reject(new Error('Request blocked by security policy'));
            }
            
            return originalFetch.apply(window, args);
        };
        
        // Monitor XHR requests
        const originalXHRSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.send = function(...args) {
            const url = this._url || this.responseURL;
            
            if (url && this.isBlockedURL(url)) {
                console.error('Blocked XHR request to:', url);
                return;
            }
            
            return originalXHRSend.apply(this, args);
        };
    }

    isBlockedURL(url) {
        const blockedPatterns = [
            /evil\.com/,
            /malicious\.site/,
            /hack\./,
            /phishing/
        ];
        
        return blockedPatterns.some(pattern => pattern.test(url));
    }

    protectConsole() {
        // Detect console opening
        let devtools = { open: false, orientation: null };
        const threshold = 160;
        
        setInterval(() => {
            if (window.outerHeight - window.innerHeight > threshold || 
                window.outerWidth - window.innerWidth > threshold) {
                if (!devtools.open) {
                    devtools.open = true;
                    this.handleDevToolsOpen();
                }
            } else {
                devtools.open = false;
            }
        }, 500);
        
        // Protect against console.log hijacking
        const originalLog = console.log;
        console.log = (...args) => {
            // Filter sensitive information
            const filtered = args.map(arg => this.filterSensitiveData(arg));
            return originalLog.apply(console, filtered);
        };
    }

    filterSensitiveData(data) {
        if (typeof data === 'string') {
            // Remove potential sensitive patterns
            return data.replace(/\b\d{3,4}\b/g, '***') // CVV
                      .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '**** **** **** ****') // Credit card
                      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '***@***.***'); // Email
        }
        return data;
    }

    detectDebugger() {
        const check = () => {
            const start = performance.now();
            debugger;
            const end = performance.now();
            
            if (end - start > 100) {
                this.handleDebuggerDetected();
            }
        };
        
        setInterval(check, 5000);
    }

    handleDevToolsOpen() {
        console.warn('Developer tools detected');
        this.suspiciousActivity++;
        
        if (window.analytics) {
            window.analytics.trackEvent('Security', 'DevToolsOpened', window.location.pathname);
        }
    }

    handleDebuggerDetected() {
        console.warn('Debugger detected');
        this.suspiciousActivity++;
    }

    setupXSSProtection() {
        // Sanitize all text content
        this.sanitizer = {
            escapeHTML: (str) => {
                const div = document.createElement('div');
                div.textContent = str;
                return div.innerHTML;
            },
            
            sanitizeHTML: (html) => {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                
                // Remove script tags
                const scripts = tempDiv.getElementsByTagName('script');
                for (let i = scripts.length - 1; i >= 0; i--) {
                    scripts[i].parentNode.removeChild(scripts[i]);
                }
                
                // Remove event handlers
                const allElements = tempDiv.getElementsByTagName('*');
                for (let i = 0; i < allElements.length; i++) {
                    const attrs = allElements[i].attributes;
                    for (let j = attrs.length - 1; j >= 0; j--) {
                        if (attrs[j].name.startsWith('on')) {
                            allElements[i].removeAttribute(attrs[j].name);
                        }
                    }
                }
                
                return tempDiv.innerHTML;
            }
        };
        
        // Make sanitizer globally available
        window.sanitizer = this.sanitizer;
    }

    setupRateLimiting() {
        this.rateLimiter = {
            check: (key, limit = 10, window = 60000) => {
                const now = Date.now();
                const userLimits = this.rateLimits.get(key) || [];
                
                // Remove old entries
                const validLimits = userLimits.filter(time => now - time < window);
                
                if (validLimits.length >= limit) {
                    return false; // Rate limit exceeded
                }
                
                validLimits.push(now);
                this.rateLimits.set(key, validLimits);
                return true;
            },
            
            reset: (key) => {
                this.rateLimits.delete(key);
            }
        };
    }

    setupClickjackingProtection() {
        // Check if we're in an iframe
        if (window.self !== window.top) {
            // Attempt to break out
            try {
                window.top.location = window.self.location;
            } catch (e) {
                // If we can't break out, make the page unusable
                document.body.innerHTML = '';
                console.error('Clickjacking attempt detected and blocked');
                this.handleClickjackingAttempt();
            }
        }
        
        // Add frame-busting CSS
        const style = document.createElement('style');
        style.textContent = `
            html { display: none !important; }
        `;
        document.head.appendChild(style);
        
        // Remove CSS if not in frame
        if (window.self === window.top) {
            style.remove();
        }
    }

    handleClickjackingAttempt() {
        const violation = {
            type: 'Clickjacking',
            timestamp: new Date().toISOString(),
            referrer: document.referrer
        };
        
        this.violations.push(violation);
        
        if (window.analytics) {
            window.analytics.trackEvent('Security', 'ClickjackingAttempt', document.referrer);
        }
    }

    setupSuspiciousActivityDetection() {
        // Monitor for rapid clicks (bot behavior)
        let clickCount = 0;
        let clickTimer;
        
        document.addEventListener('click', () => {
            clickCount++;
            
            if (clickCount > 20) {
                console.warn('Suspicious click activity detected');
                this.suspiciousActivity += 5;
            }
            
            clearTimeout(clickTimer);
            clickTimer = setTimeout(() => {
                clickCount = 0;
            }, 1000);
        });
        
        // Monitor for copy attempts of sensitive data
        document.addEventListener('copy', (e) => {
            const selection = window.getSelection().toString();
            if (this.containsSensitiveData(selection)) {
                e.preventDefault();
                console.warn('Attempted to copy sensitive data');
                this.suspiciousActivity++;
            }
        });
        
        // Monitor for automated behavior
        this.detectAutomation();
    }

    containsSensitiveData(text) {
        const patterns = [
            /\b\d{3,4}\b/, // CVV
            /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/, // Credit card
            /\b(?:password|passwd|pwd)\s*[:=]\s*\S+/i // Passwords
        ];
        
        return patterns.some(pattern => pattern.test(text));
    }

    detectAutomation() {
        const tests = [
            () => navigator.webdriver,
            () => navigator.plugins.length === 0,
            () => navigator.languages.length === 0,
            () => !window.chrome && !!window.chrome.runtime,
            () => document.documentElement.getAttribute('webdriver'),
            () => window.domAutomation || window.domAutomationController
        ];
        
        const automationDetected = tests.some(test => {
            try {
                return test();
            } catch {
                return false;
            }
        });
        
        if (automationDetected) {
            console.warn('Automation tools detected');
            this.suspiciousActivity += 10;
        }
    }

    setupSecurityHeaders() {
        // These headers should ideally be set server-side
        this.securityHeaders = {
            'X-Content-Type-Options': 'nosniff',
            'X-Frame-Options': 'DENY',
            'X-XSS-Protection': '1; mode=block',
            'Referrer-Policy': 'strict-origin-when-cross-origin',
            'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
            'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload'
        };
    }

    // Public API methods
    
    checkRateLimit(action, userId = 'anonymous') {
        const key = `${action}:${userId}`;
        return this.rateLimiter.check(key);
    }

    sanitizeInput(input) {
        if (typeof input !== 'string') return input;
        
        return input
            .replace(/[<>]/g, '') // Remove potential HTML tags
            .replace(/javascript:/gi, '') // Remove javascript: protocol
            .replace(/on\w+\s*=/gi, '') // Remove event handlers
            .replace(/eval\s*\(/gi, '') // Remove eval
            .replace(/expression\s*\(/gi, ''); // Remove CSS expressions
    }

    sanitizeHTML(html) {
        return this.sanitizer.sanitizeHTML(html);
    }

    escapeHTML(text) {
        return this.sanitizer.escapeHTML(text);
    }

    validateURL(url) {
        try {
            const parsed = new URL(url);
            
            // Check protocol
            if (!['http:', 'https:'].includes(parsed.protocol)) {
                return false;
            }
            
            // Check for local/private IPs
            const hostname = parsed.hostname;
            if (hostname === 'localhost' || 
                hostname === '127.0.0.1' ||
                hostname.startsWith('192.168.') ||
                hostname.startsWith('10.') ||
                hostname.startsWith('172.')) {
                return false;
            }
            
            return true;
        } catch {
            return false;
        }
    }

    getCSRFToken() {
        return this.csrfToken;
    }

    reportViolation(type, details) {
        const violation = {
            type,
            details,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent
        };
        
        this.violations.push(violation);
        
        // Send to server if configured
        if (window.siteConfig?.securityReportingEndpoint) {
            this.sendSecurityReport(violation);
        }
    }

    sendSecurityReport(violation) {
        fetch(window.siteConfig.securityReportingEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': this.csrfToken
            },
            body: JSON.stringify(violation)
        }).catch(error => {
            console.error('Failed to send security report:', error);
        });
    }

    isSecure() {
        return this.suspiciousActivity === 0 && 
               this.violations.length === 0 && 
               window.location.protocol === 'https:';
    }

    getSecurityStatus() {
        return {
            initialized: this.initialized,
            secure: this.isSecure(),
            violations: this.violations.length,
            suspiciousActivity: this.suspiciousActivity,
            csrfProtection: !!this.csrfToken,
            httpsEnabled: window.location.protocol === 'https:',
            headers: this.securityHeaders
        };
    }

    handleSecurityError(error) {
        console.error('Security error:', error);
        this.violations.push({
            type: 'SecurityError',
            message: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString()
        });
    }

    destroy() {
        // Clean up event listeners and observers
        this.initialized = false;
        this.violations = [];
        this.suspiciousActivity = 0;
        this.rateLimits.clear();
        this.blockedIPs.clear();
    }
}

// Create and export singleton instance
export const securityManager = new SecurityManager();

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        securityManager.init();
    });
} else {
    securityManager.init();
}

export default securityManager;